import React, { useState, useRef, useEffect, useCallback } from 'react'
import './navbar.scss'
import {
  Hamburger,
  CBLogoV2,
  Close,
  ExpandMoreIcon,
  DashboardIcon,
  ProfileIcon,
  LogoutIcon,
  FilePostIcon,
  MailOpenLineIcon,
  PatchQuestionFillIcon,
  VideoCameraIcon,
  ChartMultipleIcon,
  TotalInvestmentIcon,
  InvestingIcon,
} from '../Icon'
import { Anchor } from '../Anchor'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../store'
import { Button } from '../Button'
import { useBreakpoint } from '../../utils/hooks'
import { LG, EVENTS } from '../../utils/constants'
import { useNavigate, Link } from 'react-router-dom'
import { userServices } from '../../../services'
import { resetConfiguration } from '../../../global-state/investor-slice'
import { showBanner } from '../../../global-state/banner-slice'
import { captureEvent } from '../../utils/posthogUtils/usePostHog'
import { Tag } from '../Tag'
import { capitalizeText, getRoleString } from '../../utils/helpers'
import { FeatureName, isFeatureEnabled } from '../../utils/feature-flags'

const navLinks = [
  {
    href: '/opportunities',
    text: 'Invest',
  },
  {
    href: '',
    text: 'Public Markets',
  },
  {
    href: '/about',
    text: 'About Us',
  },
  {
    href: '/contact-us',
    text: 'Reach Us',
  },
  {
    href: '',
    text: 'Resources',
  },
]
const subLinks = [
  {
    value: '/dashboard',
    text: 'Dashboard',
    icon: <DashboardIcon />,
  },
  {
    value: '/profile/general',
    text: 'Profile',
    icon: <ProfileIcon />,
  },
  {
    value: '#',
    text: 'Logout',
    icon: <LogoutIcon />,
  },
]

const learnLinks = [
  {
    href: '/insights',
    text: 'Insights',
    Icon: FilePostIcon,
    description: 'Our latest industry insights and company news',
  },
  {
    href: '/newsletter',
    text: 'Newsletter',
    Icon: MailOpenLineIcon,
    description: 'The Credibila Insider',
  },
  {
    href: '/webinars',
    text: 'Webinars',
    Icon: VideoCameraIcon,
    description: 'On-demand sessions and expert conversations',
  },
]

const helpLinks = [
  {
    href: '/faq',
    text: 'FAQ',
    Icon: PatchQuestionFillIcon,
    description: 'Frequently asked questions and answers',
  },
]

type DropdownLink = {
  href: string
  text: string
  Icon: React.ComponentType
  description: string
  feature?: FeatureName
}
type DropdownSection = { title: string; links: DropdownLink[] }

const resourcesSections: DropdownSection[] = [
  { title: 'Learn', links: learnLinks },
  { title: 'Help', links: helpLinks },
]

const publicMarketsSections: DropdownSection[] = [
  {
    title: 'Public Markets',
    links: [
      {
        href: '/dashboard/trading',
        text: 'Trading',
        Icon: () => <ChartMultipleIcon width='20' height='20' />,
        description: 'Buy and sell US-listed stocks and ETFs',
        feature: 'trading',
      },
      {
        href: '/dashboard/trading/portfolio',
        text: 'Portfolio',
        Icon: () => <InvestingIcon width='20' height='20' />,
        description: 'Your holdings, gains and losses over time',
        feature: 'trading',
      },
      {
        href: '/dashboard/wallet-public',
        text: 'Wallet',
        Icon: () => <TotalInvestmentIcon width='20' height='20' />,
        description: 'Cash, transfers and your stock portfolio value',
        feature: 'publicWallet',
      },
    ],
  },
]

/** Header menus that open a dropdown instead of linking somewhere. */
const DROPDOWN_SECTIONS: Record<string, DropdownSection[]> = {
  Resources: resourcesSections,
  'Public Markets': publicMarketsSections,
}

/** Drops feature-flagged links that are off, and sections left empty. */
const visibleSections = (sections: DropdownSection[]): DropdownSection[] =>
  sections
    .map((section) => ({
      ...section,
      links: section.links.filter((link) => !link.feature || isFeatureEnabled(link.feature)),
    }))
    .filter((section) => section.links.length > 0)

/** Menus only shown to signed-in investors. */
const LOGGED_IN_ONLY = ['Public Markets']

const NavigationDropDown = ({
  displayName,
  logoutHandler,
  isEmailVerified,
}: {
  displayName: string
  logoutHandler: () => void
  isEmailVerified: boolean
}) => {
  const [active, setActive] = useState(false)
  const navigationDropdownRef = useRef<HTMLDivElement | null>(null)
  const dispatch = useDispatch()

  useEffect(() => {
    document.addEventListener('click', (e: Event) => {
      if (
        navigationDropdownRef.current &&
        !navigationDropdownRef.current.contains(e.target as Node)
      )
        setActive(false)
    })
  }, [navigationDropdownRef])

  const navigate = useNavigate()
  return (
    <div className='select'>
      <div className='selectWrapper'>
        <div
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setActive(!active)
          }}
          className={`selectCustom js-selectCustom ${active ? 'isActive' : ''}`}
        >
          <div className='selectCustom-trigger'>
            {displayName}{' '}
            <ExpandMoreIcon classes={active ? ['expand-more-icon', 'ml-12'] : ['ml-12']} />{' '}
          </div>
          <div ref={navigationDropdownRef} className='selectCustom-options'>
            {subLinks.map(({ text, value, icon }) => (
              <div key={value}>
                {text !== 'Logout' && (
                  <div
                    onClick={() => {
                      if (isEmailVerified) navigate(value)
                      else
                        dispatch(
                          showBanner({
                            text: `Please verify your email address to view the ${text}.`,
                            variant: 'warning-banner',
                          }),
                        )
                    }}
                    className='selectCustom-option'
                    data-value={value}
                  >
                    {icon} {text}
                  </div>
                )}
                {text === 'Logout' && (
                  <div
                    onClick={() => {
                      captureEvent(EVENTS.user_logged_out, { section: text })
                      logoutHandler()
                    }}
                    className='selectCustom-option-logout'
                    data-value={value}
                  >
                    {icon} {text}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// Resources Dropdown Component.
const ResourcesDropDown = ({
  text,
  sections,
  isLoggedIn,
  isNavExpanded,
  setIsNavExpanded,
}: {
  text: string
  sections: DropdownSection[]
  isLoggedIn: string
  isNavExpanded: boolean
  setIsNavExpanded: (val: boolean) => void
}) => {
  // Deps.
  const navigate = useNavigate()

  // Refs.
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const resourcesDropdownRef = useRef<HTMLDivElement | null>(null)

  // States.
  const [showResourcesDropdown, setShowResourcesDropdown] = useState(false)
  const [showResourcesDropdownMobile, setShowResourcesDropdownMobile] = useState(false)

  // Effects.
  useEffect(() => {
    const handleClickOutside = (e: Event) => {
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(e.target as Node))
        setShowResourcesDropdown(false)
    }
    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [resourcesDropdownRef])

  useEffect(() => {
    if (!isNavExpanded && showResourcesDropdownMobile) {
      setShowResourcesDropdownMobile(false)
    }
  }, [isNavExpanded, showResourcesDropdownMobile])

  // Handlers.
  const clearTimeoutRef = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])
  const handleMouseEnter = useCallback(() => {
    setShowResourcesDropdown(true)
    clearTimeoutRef()
  }, [clearTimeoutRef])
  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setShowResourcesDropdown(false)
      clearTimeoutRef()
    }, 300)
  }, [clearTimeoutRef])

  const ResourcesDropdownItem = ({
    href,
    text,
    Icon,
    description,
  }: {
    href: string
    text: string
    Icon: React.ReactNode
    description: string
  }) => {
    return (
      <div
        onClick={() => navigate(href)}
        className='resources-dropdown-item-link'
        data-value={href}
        key={href}
      >
        <div className='resources-dropdown-item-link-icon'>{Icon}</div>
        <div className='resources-dropdown-item-link-content'>
          <div className='resources-dropdown-item-link-text'>{text}</div>
          <div className='resources-dropdown-item-link-description'>{description}</div>
        </div>
      </div>
    )
  }

  const ResourcesDropdownItemMobile = ({
    href,
    description,
    text,
  }: {
    href: string
    text: string
    description: string
  }) => {
    return (
      <div
        onClick={() => {
          navigate(href)
          setShowResourcesDropdownMobile(false)
          if (isNavExpanded) setIsNavExpanded(false)
        }}
        className='resources-dropdown-item-link resources-dropdown-mobile-item-link'
        data-value={href}
        key={href}
      >
        <div className='resources-dropdown-mobile-item-link-content'>
          <div className='resources-dropdown-item-link-text'>{text}</div>
          <div className='resources-dropdown-item-link-description'>{description}</div>
        </div>
      </div>
    )
  }

  // UI.
  return (
    <>
      {/* Desktop Resources Dropdown. */}
      <li
        className='nav-item resources-dropdown-container'
        key={text + '-desktop'}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className='resources-dropdown-trigger'>
          <Anchor isDisabled={!isLoggedIn} href='#' text={text} variant='nav-link' />
          <ExpandMoreIcon
            classes={
              showResourcesDropdown
                ? ['resources-dropdown-trigger-icon', 'rotate-180']
                : ['resources-dropdown-trigger-icon']
            }
            height='16'
            width='16'
          />
        </div>

        <div
          ref={resourcesDropdownRef}
          className={`resources-dropdown ${showResourcesDropdown ? 'show' : ''}`}
        >
          <div className='resources-dropdown-content'>
            {sections.map((section) => (
              <div className='resources-dropdown-item' key={section.title}>
                <p className='resources-dropdown-item-title'>{section.title}</p>
                {section.links.map(({ href, text, Icon, description }) => (
                  <ResourcesDropdownItem
                    key={href}
                    href={href}
                    text={text}
                    Icon={<Icon />}
                    description={description}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </li>

      {/* Mobile Resources Dropdown. */}
      <li
        key={text + '-mobile'}
        className='nav-item resources-dropdown-mobile-container'
        onClick={() => setShowResourcesDropdownMobile(!showResourcesDropdownMobile)}
      >
        <div className='resources-dropdown-mobile-trigger'>
          <Anchor href='#' text={text} variant='nav-link' />
          <ExpandMoreIcon
            classes={
              showResourcesDropdownMobile
                ? ['resources-dropdown-trigger-icon', 'rotate-180']
                : ['resources-dropdown-trigger-icon']
            }
            height='16'
            width='16'
          />
        </div>
        <div className={`resources-dropdown-mobile ${showResourcesDropdownMobile ? 'show' : ''}`}>
          {sections.map((section) => (
            <div className='resources-dropdown-mobile-content' key={section.title}>
              <div className='resources-dropdown-mobile-item'>
                <p className='resources-dropdown-item-title'>{section.title}</p>
                {section.links.map(({ href, text, description }) => (
                  <ResourcesDropdownItemMobile
                    key={href}
                    href={href}
                    description={description}
                    text={text}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </li>
    </>
  )
}

export const Navbar = ({
  setShowContactUsModal,
}: {
  setShowContactUsModal: (val: boolean) => void
}) => {
  const { authToken, name, email, isEmailVerified, adminRole } = useSelector(
    (state: RootState) => state.investor,
  )
  const [isNavExpanded, setIsNavExpanded] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const loggedIn = authToken && authToken
  const { breakpoint } = useBreakpoint()
  const logoutHandler = async () => {
    const data = await userServices.userLogout_v1({
      Authorization: authToken ? authToken : '',
    })
    if (data.status === 200) {
      dispatch(resetConfiguration())
      window.location.reload()
    }
  }

  const firstName = name?.split(' ')?.[0]

  return (
    <div className='navigation-wrapper'>
      <nav className='navigation'>
        <Link
          to={'/home'}
          onClick={() => captureEvent(EVENTS.navigated_to_home, { button: 'Home' })}
        >
          <div className='logo-container'>
            <CBLogoV2 classes={['navigation-logo']} width={'168px'} />
            {authToken && adminRole && (
              <Tag
                text={`${capitalizeText(getRoleString(adminRole))} Mode`}
                variant='golden-bg'
                classes={['admin-mode-logo-badge']}
              />
            )}
          </div>
        </Link>
        <button
          className='hamburger'
          onClick={() => {
            setIsNavExpanded(!isNavExpanded)
          }}
        >
          {!isNavExpanded && <Hamburger />}
          {isNavExpanded && <Close />}
        </button>
        <div
          className={
            isNavExpanded
              ? 'navigation-menu expanded'
              : `navigation-menu ${loggedIn ? 'loggedIn' : ''}`
          }
        >
          <ul className='nav-list'>
            {!loggedIn && breakpoint !== undefined && breakpoint <= LG && (
              <>
                <li
                  className='nav-item'
                  onClick={() => {
                    captureEvent(EVENTS.navigated_to_signup_page, {
                      button: 'Login',
                    })
                    if (isNavExpanded) setIsNavExpanded(false)
                  }}
                >
                  {isNavExpanded && (
                    <Anchor href={'/login'} text={'Login'} variant='nav-link-bold' />
                  )}
                </li>
                <li
                  className='nav-item'
                  onClick={() => {
                    captureEvent(EVENTS.navigated_to_signup_page, {
                      button: 'Sign up',
                    })
                    if (isNavExpanded) setIsNavExpanded(false)
                  }}
                >
                  {isNavExpanded && (
                    <Anchor href={'/signup'} text={'Sign up'} variant='nav-link-bold' />
                  )}
                </li>
              </>
            )}
            {navLinks
              .filter((obj) => {
                if (!loggedIn && LOGGED_IN_ONLY.includes(obj.text)) return false
                if (!loggedIn && obj.href !== '/saved-opportunities') return obj
                if (loggedIn) return obj
              })
              .map(({ href, text }, index) => {
                if (DROPDOWN_SECTIONS[text]) {
                  const sections = visibleSections(DROPDOWN_SECTIONS[text])
                  if (sections.length === 0) return null
                  return (
                    <ResourcesDropDown
                      key={text + index}
                      text={text}
                      sections={sections}
                      isLoggedIn={loggedIn}
                      isNavExpanded={isNavExpanded}
                      setIsNavExpanded={setIsNavExpanded}
                    />
                  )
                } else {
                  return (
                    <li
                      className='nav-item'
                      key={href}
                      onClick={() => {
                        if (isNavExpanded) setIsNavExpanded(false)
                        if (!loggedIn && text === 'Reach Us') {
                          setShowContactUsModal(true)
                        }
                      }}
                    >
                      <Anchor
                        isDisabled={!loggedIn && text === 'Reach Us'}
                        href={href}
                        text={text}
                        variant='nav-link'
                      />
                    </li>
                  )
                }
              })}
            {!loggedIn && (breakpoint === undefined || breakpoint > LG) && (
              <>
                <li
                  className='nav-item'
                  onClick={() => {
                    captureEvent(EVENTS.navigated_to_signup_page, {
                      button: 'Login',
                    })
                    if (isNavExpanded) setIsNavExpanded(false)
                  }}
                >
                  {isNavExpanded && (
                    <Anchor href={'/login'} text={'Login'} variant='nav-link-bold' />
                  )}
                </li>
                <li
                  className='nav-item'
                  onClick={() => {
                    captureEvent(EVENTS.navigated_to_signup_page, {
                      button: 'Sign up',
                    })
                    if (isNavExpanded) setIsNavExpanded(false)
                  }}
                >
                  {isNavExpanded && (
                    <Anchor href={'/signup'} text={'Sign up'} variant='nav-link-bold' />
                  )}
                </li>
              </>
            )}

            {loggedIn && breakpoint && breakpoint <= LG && (
              <>
                <li
                  className='nav-item'
                  onClick={() => {
                    captureEvent(EVENTS.navigated_to_dashboard, {
                      button: 'Dashboard',
                    })
                    if (isNavExpanded) setIsNavExpanded(false)
                  }}
                >
                  <Anchor href='/dashboard' text='Dashboard' variant='nav-link' />
                </li>
                <li
                  className='nav-item'
                  onClick={() => {
                    captureEvent(EVENTS.navigated_to_profile, {
                      button: 'Profile',
                    })
                    if (isNavExpanded) setIsNavExpanded(false)
                  }}
                >
                  <Anchor href='/profile/general' text='Profile' variant='nav-link' />
                </li>
                <li className='nav-item'>
                  <div
                    onClick={() => {
                      logoutHandler()
                    }}
                  >
                    <Anchor href='' text='Logout' variant='nav-link-bold' />
                  </div>
                </li>
              </>
            )}
          </ul>
        </div>

        {!loggedIn && breakpoint && breakpoint >= LG && (
          <div className='navbar-auth-buttons'>
            <Button
              text='Login'
              classes={['navbar-btn-padding']}
              variant='primary-btn'
              handleClick={() => {
                captureEvent(EVENTS.navigated_to_signup_page, {
                  button: 'Login',
                })
                navigate('/login')
              }}
            />
            <Button
              text='Sign up'
              classes={['navbar-btn-padding']}
              variant='secondary-btn'
              handleClick={() => {
                captureEvent(EVENTS.navigated_to_signup_page, {
                  button: 'Sign up',
                })
                navigate('/signup')
              }}
            />
          </div>
        )}

        {loggedIn && breakpoint && breakpoint >= LG && (
          <div className='nav-item'>
            <NavigationDropDown
              logoutHandler={logoutHandler}
              displayName={firstName?.length > 0 ? firstName : email}
              isEmailVerified={isEmailVerified}
            />
          </div>
        )}
      </nav>
    </div>
  )
}
